import { HttpClient, HttpHeaders } from '@angular/common/http';

import { HttpUtils } from '../utils/httpUtils';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';
import { CalibrationBalance, CalibrationBalanceAuditFilter, CalibrationBalanceFilter } from '../model/calibrationBalance';
import { CalibrationBalanceReportFilter } from '../model/attachment';

@Injectable()
export class CalibrationBalanceService {

    readonly baseUrl =  `${environment.balancesUrl}/calibration`;
    readonly baseUrlReport =  `${environment.balancesUrl}/report`;

    constructor(private http: HttpClient) {
    }

    getAll(filter: CalibrationBalanceFilter): Observable<any> {
        return this.http.post(`${this.baseUrl}/`, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    getOne(id: number): Observable<any> {
        return this.http.get(`${this.baseUrl}/${id}`).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    delete(id: number): Observable<any> {
        return this.http.delete(`${this.baseUrl}/${id}`).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    save(calibration: CalibrationBalance, token: string): Observable<any> {
        let url = `${this.baseUrl}/`;

        const form = new FormData();
        form.append('calibration', JSON.stringify(calibration));
        form.append('token', token);

        if (calibration.id !== null && calibration.id !== undefined) {
            url += calibration.id;
            return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        } else {
            url += 'create';
            return this.http.post(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        }
    }

    getUncertaintyFromEquipment(idEquipment: number, idVariable: number): Observable<any> {
        const url = `${this.baseUrl}/equipment/${idEquipment}/variable/${idVariable}/uncertainty`;

        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    getInterpolateValuePattern(idPattern: number, idVariable: number, value: number): Observable<any> {
        const url = `${this.baseUrl}/equipment/${idPattern}/variable/${idVariable}/uncertainty/interpolate/${value}`;

        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }
    
    getMassFromEquipment(idEquipment: number, idVariable: number): Observable<any> {
        const url = `${this.baseUrl}/equipment/${idEquipment}/variable/${idVariable}/mass`;

        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    downloadPdfCalibrationVersion(calibration: CalibrationBalance): Observable<any> {
        return this.downloadPdfVersion(calibration.id, calibration.signedVersion);
    }

    downloadPdfVersion(idCalibration: number, version: number): Observable<any> {
        const url = `${this.baseUrlReport}/calibration/${idCalibration}/version/${version}`;

        return this.http.post(url, {}, { responseType: 'blob' });
    }

    downloadPdf(id: number, config: CalibrationBalanceReportFilter = new CalibrationBalanceReportFilter()): Observable<any> {
        const url = `${this.baseUrlReport}/calibration/${id}`;

        const lang: string = config.lang || 'es';
        const headers = new HttpHeaders({ lang });

        const data = {
            toSign: config.toSign,
            reason: config.reason
        };

        return this.http.post(url, data, { responseType: 'blob', headers });
    }

    checkCopyToGroup(id: number, idGroup: number): Observable<any> {
        const url = `${this.baseUrl}/clonate/${id}/toGroup/${idGroup}/check`;

        return this.http.post(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    copyToGroup(id: number, idGroup: number): Observable<any> {
        const url = `${this.baseUrl}/clonate/${id}/toGroup/${idGroup}`;

        return this.http.put(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    copyToGroupWithFolder(id: number, idGroup: number, idFolder: number): Observable<any> {
        const url = `${this.baseUrl}/clonate/${id}/toGroup/${idGroup}/folder/${idFolder}`;

        return this.http.put(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    manualSign(calibration: CalibrationBalance, document: File, token: string): Observable<any> {
        const url = `${this.baseUrl}/${calibration.id}/manualSign`;

        const form = new FormData();
        form.append('document', document, document.name);
        form.append('calibration', JSON.stringify(calibration));
        form.append('token', token);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    automaticSign(idCalibration: number, token: string, lang?: string): Observable<any> {
        const url = `${this.baseUrl}/${idCalibration}/automaticSign`;

        const form = new FormData();
        form.append('token', token);

        const headers = new HttpHeaders({ lang });

        return this.http.put(url, form, { headers }).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    automaticSignBulk(ids: number[], token: string, lang?: string): Observable<any> {
        const url = `${this.baseUrl}/bulk/automaticSign`;

        const form = new FormData();
        form.append('ids', ids.join(';'));
        form.append('token', token);

        const headers = new HttpHeaders({ lang });

        return this.http.put(url, form, { headers }).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    advance(calibration: CalibrationBalance, token: string): Observable<any> {
        const url = `${this.baseUrl}/${calibration.id}/advance`;

        const form = new FormData();
        form.append('token', token);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    back(calibration: CalibrationBalance, token: string): Observable<any> {
        const url = `${this.baseUrl}/${calibration.id}/back`;

        const form = new FormData();
        form.append('token', token);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    revertSign(calibration: CalibrationBalance, reason: string, token: string): Observable<any> {
        const url = `${this.baseUrl}/${calibration.id}/revertSign`;

        const form = new FormData();
        form.append('token', token);
        form.append('reason', reason);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    getAudit(id: number, filter: CalibrationBalanceAuditFilter): Observable<any> {
        const url = `${this.baseUrl}/${id}/audit`;

        return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    downloadAuditPdf(id: number): Observable<any> {
        const url = `${this.baseUrlReport}/calibration/${id}/audit`;

        return this.http.get(url, { responseType: 'blob' });
    }

    invalidate(id: number, token: string, reason: string): Observable<any> {
        const url = `${this.baseUrl}/${id}/invalidate`;

        const form = new FormData();
        form.append('token', token);
        form.append('reason', reason);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    getAllFromEquipment(id: number): Observable<any> {
        const url = `${this.baseUrl}/equipment/${id}`;

        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

}
